import { CheckCircle2 } from 'lucide-react';
import Image from 'next/image';
import { OurValues } from '@/lib/aboutPage';

const OurValuesSection = () => {
    return (
        <section 
            id="our-values"
            className="py-16 lg:min-h-[800px] px-6 overflow-hidden flex items-center bg-white"
            aria-labelledby="values-heading"
        >
            <div className="max-w-7xl mx-auto w-full flex flex-col lg:flex-row items-center gap-12 lg:gap-16">

                {/* Left Side: Content */}
                <div className="w-full lg:w-1/2">
                    {/* Header Grouping for SEO */}
                    <header className="text-center lg:text-left">
                        <div className="flex items-center justify-center lg:justify-start gap-2 text-blue-600 font-bold mb-4">
                            <span aria-hidden="true" className="text-xl">»</span>
                            <span className="uppercase tracking-widest text-[10px] sm:text-xs">
                                Why choose us
                            </span>
                        </div>

                        <h2 
                            id="values-heading"
                            className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#001D4A] mb-6 leading-tight"
                        >
                            {OurValues.title}
                        </h2>

                        <p className="text-gray-600 mb-10 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                            {OurValues.description}
                        </p>
                    </header>

                    {/* Features List - Semantic List for Screen Readers */}
                    <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-6 mb-10 list-none p-0">
                        {OurValues.values.map((item, index) => (
                            <li key={index} className="flex gap-4 group items-start">
                                <div className="flex-shrink-0 pt-1" aria-hidden="true">
                                    {/* Improved contrast: Using blue-400 base for accessibility */}
                                    <CheckCircle2 className="w-6 h-6 text-blue-400 group-hover:text-blue-600 transition-colors duration-300" />
                                </div>

                                <div className="space-y-1">
                                    <h3 className="font-bold text-gray-900 text-base sm:text-lg">
                                        {item.title}
                                    </h3>
                                    <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                                        {item.description}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Right Side: Image */}
                <div className="w-full lg:w-1/2 h-[350px] sm:h-[450px] lg:h-[600px] relative">
                    <div className="relative h-full w-full overflow-hidden rounded-2xl shadow-2xl bg-slate-100">
                        <Image
                            src={OurValues.imageUrl}
                            alt="Our professional team collaborating in a modern office environment"
                            fill
                            className="object-cover grayscale-[20%] hover:grayscale-0 transition-all duration-700 ease-in-out"
                            sizes="(max-width: 1024px) 100vw, 40vw"
                            loading="lazy"
                        />
                        {/* Interactive overlay */}
                        <div className="hidden lg:block absolute inset-0 bg-gradient-to-tr from-blue-600/10 via-transparent to-transparent pointer-events-none" aria-hidden="true" />
                    </div>
                    
                    {/* Floating Experience Badge */}
                    <div 
                        className="absolute -bottom-6 -right-2 sm:right-6 bg-white p-6 rounded-xl shadow-xl hidden sm:flex items-center gap-4 border border-slate-100"
                        role="status"
                        aria-label="Over 10 years of excellence"
                    >
                        <div className="text-4xl font-black text-blue-600">10+</div>
                        <div className="text-[10px] uppercase font-bold tracking-tighter text-gray-500 leading-none">
                            Years of <br /> Excellence
                        </div>
                    </div>
                </div>

            </div>
        </section>
    );
};

export default OurValuesSection;